import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { apiDomain } from "configs/configs";
import { logout } from "helpers/helpers";
import {
  Form,
  Typography,
  Checkbox,
  notification,
  Button,
  Space,
  Select,
} from "antd";
import styles from "pages/seniorMaster/volumeConfirmation/volumeConfirmation.module.scss";
import { PlusOutlined, MinusOutlined } from "@ant-design/icons";
import MobileInnerWrapper from "layouts/mobileInnerWrapper/mobileInnerWrapper";
import Table, { ColumnsType } from "components/table/table";
import useVolumeConfirmationStore from "stores/seniorMaster/volumeConfirmation";
import {
  MeasureTableElementType,
  MeasureType,
  ObjectType,
} from "pages/admin/objectsAndClients/objects/types";
import clsx from "clsx";

type ComponentProps = {};

const { Text, Title } = Typography;
const { Option } = Select;

const Page: React.FC<ComponentProps> = () => {
  const {
    list,
    setList,
    isPostInProcess,
    setIsPostInProcess,
    selectedObjectIndex,
    setSelectedObjectIndex,
    isError,
    setIsError,
  } = useVolumeConfirmationStore((store) => store);

  const listRef = useRef(list);

  //  Error while saving change status
  const openNotificationWithError = useCallback(
    ({ message }: { message: string }) => {
      notification.error({
        message: "Ошибка",
        description: message,
        placement: "topRight",
        duration: 3,
      });
    },
    []
  );

  //  Success message
  const openNotificationWithSuccess = useCallback(
    ({ message }: { message: string }) => {
      notification.success({
        message: "Сохранено",
        description: message,
        placement: "topRight",
        duration: 3,
      });
    },
    []
  );

  //  Save current list to the ref so we can use it in useEffect without trigger it
  useEffect(() => {
    listRef.current = list;
  }, [list]);

  //  Get all knowledge base items
  const getList = useCallback(
    async (isRefresh?: boolean) => {
      const fetchData = async () => {
        try {
          const url = `${apiDomain}/api/records/objects`;
          const options: RequestInit = {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
          };

          const response = await fetch(url, options);

          if (!response.ok) throw new Error();

          const data = await response.json();

          if (!data?.records) throw new Error();

          const records: ObjectType[] = data.records;

          setList({ isLoaded: true, data: records });
        } catch (error) {
          logout();
        }
      };

      if (listRef.current.isLoaded && !isRefresh) return;

      if (!isRefresh) setSelectedObjectIndex(0);

      await fetchData();
    },
    [setList, setSelectedObjectIndex]
  );

  //  When started, get all data
  useEffect(() => {
    getList();
  }, [getList]);

  const isInProcess = useMemo(() => {
    if (!!isPostInProcess) return true;

    return false;
  }, [isPostInProcess]);

  const selectedItem = useMemo(() => {
    if (!list.isLoaded) return undefined;

    if (!list.data?.length) return undefined;

    const item = list.data.find((_, index) => index === selectedObjectIndex);

    if (!item) return undefined;

    return item;
  }, [list, selectedObjectIndex]);

  const measuresList = useMemo(() => {
    if (!selectedItem) return [];

    return selectedItem.measures;
  }, [selectedItem]);

  const stagesList = useMemo(() => {
    if (!selectedItem) return [];

    return selectedItem.stages;
  }, [selectedItem]);

  const setMeasuresList = useCallback(
    (setMeasuresListFunction: (state: MeasureType[]) => MeasureType[]) => {
      setList((list) => {
        return {
          ...list,
          data: [...list.data].map((item, index) => {
            if (selectedObjectIndex !== index) return item;

            return {
              ...item,
              measures: setMeasuresListFunction(item.measures),
            };
          }),
        };
      });
    },
    [setList, selectedObjectIndex]
  );

  const setStagesList = useCallback(
    (
      setStagesListFunction: (
        state: ObjectType["stages"]
      ) => ObjectType["stages"]
    ) => {
      setList((list) => {
        return {
          ...list,
          data: [...list.data].map((item, index) => {
            if (selectedObjectIndex !== index) return item;

            return {
              ...item,
              stages: setStagesListFunction(item.stages),
            };
          }),
        };
      });
    },
    [setList, selectedObjectIndex]
  );

  //  Submit form, depends of mode
  const handleFinish = useCallback(() => {
    if (isInProcess) return;
    if (!selectedItem) return;

    const fetchData = async () => {
      setIsError(false);

      setIsPostInProcess(true);

      const url = `${apiDomain}/api/records/objects/${selectedItem?.id}`;

      const bodyData = {
        measures: selectedItem.measures,
        stages: selectedItem.stages,
      };

      const options: RequestInit = {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(bodyData),
        credentials: "include",
      };

      try {
        const response = await fetch(url, options);

        if (!response.ok) throw new Error();

        openNotificationWithSuccess({ message: "Сохранение завершено" });

        await getList(true);

        setIsPostInProcess(false);
      } catch (error) {
        setIsError(true);

        openNotificationWithError({
          message: "Произошла ошибка при сохранении объекта",
        });

        setIsPostInProcess(false);
      }
    };

    fetchData();
  }, [
    selectedItem,
    isInProcess,
    setIsError,
    getList,
    setIsPostInProcess,
    openNotificationWithError,
    openNotificationWithSuccess,
  ]);

  const handleChangeChildrenVisibility = useCallback(
    ({
      type,
      id,
    }: {
      type: "group" | "building" | "element";
      id: string | number;
    }) => {
      if (type === "element") return;

      setMeasuresList((measuresList) => {
        return measuresList.map((building) => {
          return {
            ...building,
            is_children_visible:
              type === "building" && id === building.id
                ? !building?.is_children_visible
                : !!building?.is_children_visible,
            groups: (building.groups ? building.groups : []).map((group) => {
              return {
                ...group,
                is_children_visible:
                  type === "group" && id === group.id
                    ? !group.is_children_visible
                    : !!group.is_children_visible,
              };
            }),
          };
        });
      });
    },
    [setMeasuresList]
  );

  const handleChangeStageValue = useCallback(
    ({
      buildingId,
      groupId,
      elementId,
      stageId,
      operationId,
      value,
    }: {
      buildingId?: number | string;
      groupId?: number | string;
      elementId?: number | string;
      stageId: number;
      operationId?: number;
      value: boolean;
    }) => {
      setMeasuresList((measures) => {
        return measures.map((building) => {
          const groups = building.groups ? building.groups : [];

          //  Если выбрано строение
          const isAllBuildingElementsChecked =
            buildingId && buildingId.toString() === building.id.toString()
              ? building.groups?.every((group) =>
                  (group.elements ? group.elements : []).every((element) => {
                    return stagesList
                      .filter((stageItem) => stageItem.status === "active")
                      .reduce((result, stageItem) => {
                        if (result) return true;

                        if (stageItem.id.toString() !== stageId.toString())
                          return result;

                        if (operationId)
                          return element?.operations?.[stageId]
                            ? !!element.operations[stageId].find(
                                (elementOperation) => {
                                  if (
                                    elementOperation.stage_operation_id.toString() !==
                                    operationId.toString()
                                  )
                                    return false;

                                  if (!elementOperation.value) return false;

                                  return true;
                                }
                              )
                            : false;

                        return element?.operations?.[stageId]
                          ? !!element.operations[stageId].every(
                              (elementOperation) => {
                                if (!elementOperation.value) return false;

                                return true;
                              }
                            )
                          : false;
                      }, false);
                  })
                )
              : undefined;

          return {
            ...building,
            groups: groups.map((group) => {
              const elements = group.elements ? group.elements : [];

              //  Если выбрана группа
              const isAllGroupElementsChecked =
                groupId && groupId.toString() === group.id.toString()
                  ? elements.every((element) => {
                      return stagesList
                        .filter((stageItem) => stageItem.status === "active")
                        .reduce((result, stageItem) => {
                          if (result) return true;

                          if (stageItem.id.toString() !== stageId.toString())
                            return result;

                          if (operationId)
                            return element.operations?.[stageId]
                              ? !!element.operations[stageId].find(
                                  (elementOperation) => {
                                    if (
                                      elementOperation.stage_operation_id.toString() !==
                                      operationId.toString()
                                    )
                                      return false;

                                    if (!elementOperation.value) return false;

                                    return true;
                                  }
                                )
                              : false;

                          return element.operations?.[stageId]
                            ? !!element.operations[stageId].every(
                                (elementOperation) => {
                                  if (!elementOperation.value) return false;

                                  return true;
                                }
                              )
                            : false;
                        }, false);
                    })
                  : undefined;

              return {
                ...group,
                elements: elements.map((element) => {
                  const currentStageItem = stagesList.find((stageItem) => {
                    if (stageItem.status !== "active") return false;
                    if (stageItem.id !== stageId) return false;

                    return true;
                  });

                  if (!currentStageItem) return element;

                  const operations = currentStageItem.operations
                    ? currentStageItem.operations.map(
                        (currentStageItemOperation) => {
                          const elementOperation = element.operations?.[stageId]
                            ? element.operations[stageId].find((operation) => {
                                if (
                                  operation.stage_operation_id.toString() !==
                                  currentStageItemOperation.id.toString()
                                ) {
                                  return false;
                                }
                                if (!operation.value) {
                                  return false;
                                }
                                return true;
                              })
                            : undefined;

                          const operationValue = elementOperation
                            ? elementOperation.value
                            : false;

                          const isDisabled = element.operations?.[stageId]
                            ? !!element.operations[stageId].find(
                                (elementOperation) => {
                                  if (
                                    elementOperation.stage_operation_id.toString() !==
                                    currentStageItemOperation.id.toString()
                                  )
                                    return false;

                                  if (!elementOperation.value) return false;
                                  if (!elementOperation.id) return false;

                                  return true;
                                }
                              )
                            : false;

                          //  Если выбран элемент
                          const changedValue = operationId
                            ? isDisabled
                              ? operationValue
                              : elementId &&
                                elementId.toString() ===
                                  element.id.toString() &&
                                operationId.toString() ===
                                  currentStageItemOperation.id.toString()
                              ? value
                              : groupId &&
                                groupId.toString() === group.id.toString() &&
                                operationId.toString() ===
                                  currentStageItemOperation.id.toString()
                              ? !isAllGroupElementsChecked
                              : buildingId &&
                                buildingId.toString() ===
                                  building.id.toString() &&
                                operationId.toString() ===
                                  currentStageItemOperation.id.toString()
                              ? !isAllBuildingElementsChecked
                              : operationValue
                            : isDisabled
                            ? operationValue
                            : elementId &&
                              elementId.toString() === element.id.toString()
                            ? value
                            : groupId &&
                              groupId.toString() === group.id.toString()
                            ? !isAllGroupElementsChecked
                            : buildingId &&
                              buildingId.toString() === building.id.toString()
                            ? !isAllBuildingElementsChecked
                            : operationValue;

                          return {
                            id: elementOperation?.id
                              ? elementOperation.id
                              : undefined,
                            object_building_group_item_id: parseInt(
                              element.id.toString(),
                              10
                            ),
                            stage_operation_id: currentStageItemOperation.id,
                            value: changedValue,
                          };
                        }
                      )
                    : [];

                  return {
                    ...element,
                    operations: {
                      ...element.operations,
                      [currentStageItem.id]: operations,
                    },
                  };
                }),
              };
            }),
          };
        });
      });
    },
    [setMeasuresList, stagesList]
  );

  const handleChangeObjectIndex = useCallback(
    (value: string | number) => {
      if (typeof value === "string") return;

      setSelectedObjectIndex(value);
    },
    [setSelectedObjectIndex]
  );

  const handleExpandOperations = useCallback(
    (stageId: number, value: boolean) => {
      setStagesList((stagesList) => {
        return stagesList.map((stage) => {
          if (stage.id !== stageId) return stage;

          return { ...stage, is_operations_expanded: value };
        });
      });
    },
    [setStagesList]
  );

  //  Set columns for table
  const columns = useMemo(() => {
    const result: ColumnsType = [
      {
        title: "Наименование",
        key: "name",
        onHeaderCell: () => ({
          style: { verticalAlign: "top", textAlign: "left" },
        }),
        width: "15%",
        render: (text: any, untypedRecord: unknown, index) => {
          const record = untypedRecord as MeasureTableElementType;
          const styles = record.styles;

          const { isHasChildren } = {
            element: () => {
              return {
                isHasChildren: false,
              };
            },
            group: () => {
              const currentGroup = measuresList
                .flatMap((building) => building.groups || [])
                .find((group) => group.id.toString() === record.id.toString());

              const isHasChildren = !!currentGroup?.elements?.length;

              return {
                isHasChildren,
              };
            },
            building: () => {
              const currentBuilding = measuresList.find((building) => {
                return building.id.toString() === record.id.toString();
              });

              const isHasChildren = !!currentBuilding?.groups?.length;

              return { isHasChildren };
            },
            default: () => {
              return {
                isHasChildren: false,
              };
            },
          }[record.type || "default"]();

          const type = record.type;

          return (
            <Space
              size="small"
              className={clsx(
                styles?.objectName,
                !!(record.type === "group") && styles?.group,
                !!(record.type === "element") && styles?.element
              )}
              onClick={() =>
                handleChangeChildrenVisibility({
                  type: type,
                  id: record.id,
                })
              }
            >
              {!!isHasChildren ? (
                <>
                  {record.is_children_visible ? (
                    <MinusOutlined
                      className={clsx(
                        styles?.minus,
                        record.type === "element" && styles?.elementIcon
                      )}
                    />
                  ) : (
                    <PlusOutlined
                      className={clsx(
                        styles?.plus,
                        record.type === "element" && styles?.elementIcon
                      )}
                    />
                  )}
                </>
              ) : (
                <div
                  className={clsx(
                    styles?.empty,
                    record.type === "element" && styles?.elementIcon
                  )}
                ></div>
              )}
              {record.name}
            </Space>
          );
        },
      },
      {
        title: "Комментарий",
        key: "comment",
        width: "15%",
        onHeaderCell: () => ({
          style: { verticalAlign: "top", textAlign: "left" },
        }),
        render: (text: any, untypedRecord: unknown, index) => {
          const record = untypedRecord as MeasureTableElementType;

          return (
            <div>
              <Space size="small">{record.comment}</Space>
            </div>
          );
        },
      },
    ];

    stagesList.forEach((stage) => {
      if (stage.status === "unactive") return;

      if (!stage?.is_operations_expanded) {
        result.push({
          title: (
            <>
              <div className={styles.stageTitle}>
                {stage.short_name ? stage.short_name : stage.name}
              </div>
              <Button
                type="primary"
                size="small"
                className={styles.expandButton}
                onClick={() => handleExpandOperations(stage.id, true)}
                disabled={!list.isLoaded || !!isInProcess}
              >
                Развернуть
              </Button>
            </>
          ),
          key: `stage-notExpandex-${stage.id}`,
          onHeaderCell: () => ({
            style: { verticalAlign: "top", textAlign: "left" },
          }),
          render: (text: any, untypedRecord: unknown, index) => {
            const record = untypedRecord as MeasureTableElementType;

            const { isChecked, isSemiChecked, isDisabled } = {
              element: () => {
                if (!record.operations)
                  return {
                    isChecked: false,
                    isSemiChecked: false,
                    isDisabled: false,
                  };

                const isChecked = record.operations?.[stage.id]
                  ? !!record.operations[stage.id].every((recordOperation) => {
                      if (!recordOperation.value) return false;

                      return true;
                    })
                  : false;

                const isSemiChecked = isChecked
                  ? false
                  : record.operations?.[stage.id]
                  ? !!record.operations[stage.id].some((recordOperation) => {
                      if (!recordOperation.value) return false;

                      return true;
                    })
                  : false;

                const isDisabled = record.operations?.[stage.id]
                  ? !!record.operations[stage.id].every((recordOperation) => {
                      if (!recordOperation.value) return false;
                      if (!recordOperation.id) return false;

                      return true;
                    })
                  : false;

                return {
                  isChecked,
                  isSemiChecked,
                  isDisabled,
                };
              },
              group: () => {
                const currentGroup = measuresList
                  .flatMap((building) => building.groups || [])
                  .find(
                    (group) => group.id.toString() === record.id.toString()
                  );

                const isDisabled =
                  !currentGroup ||
                  !currentGroup?.elements?.length ||
                  currentGroup.elements.every((element) => {
                    if (!element.operations?.[stage.id]) return false;

                    if (
                      element.operations[stage.id].every((elementOperation) => {
                        if (!elementOperation.value) return false;
                        if (!elementOperation.id) return false;

                        return true;
                      })
                    )
                      return true;

                    return false;
                  });

                const isChecked = measuresList.reduce(
                  (buildingResult, building) => {
                    if (!building.groups?.length) return buildingResult;

                    //  Поскольку проверяется только 1 group, если в результате был успех хоть раз, возвращаем true
                    if (buildingResult) return true;

                    return building.groups.reduce((groupResult, group) => {
                      if (group.id.toString() !== record.id.toString())
                        return groupResult;

                      if (!group.elements?.length) return groupResult;

                      //  Поскольку выше сравнение с record.id, проверен будет 1 group
                      return !!group.elements.every((element) => {
                        return !!stagesList
                          .filter((stageItem) => stageItem.status === "active")
                          .reduce((result, stageItem) => {
                            if (result) return true;

                            if (stageItem.id.toString() !== stage.id.toString())
                              return result;

                            return element.operations?.[stage.id]
                              ? !!element.operations[stage.id].every(
                                  (elementOperation) => {
                                    if (!elementOperation.value) return false;

                                    return true;
                                  }
                                )
                              : false;
                          }, false);
                      });
                    }, false);
                  },
                  false
                );

                const isSemiChecked = isChecked
                  ? false
                  : measuresList.reduce((buildingResult, building) => {
                      if (!building.groups?.length) return buildingResult;

                      //  Поскольку проверяется только 1 group, если в результате был успех хоть раз, возвращаем true
                      if (buildingResult) return true;

                      //  Поскольку выше сравнение с record.id, проверен будет 1 group
                      return building.groups.reduce((groupResult, group) => {
                        if (group.id.toString() !== record.id.toString())
                          return groupResult;

                        if (!group.elements?.length) return groupResult;

                        return !!group.elements.some((element) => {
                          return !!stagesList
                            .filter(
                              (stageItem) => stageItem.status === "active"
                            )
                            .reduce((result, stageItem) => {
                              if (result) return true;

                              if (
                                stageItem.id.toString() !== stage.id.toString()
                              )
                                return result;

                              return element.operations?.[stage.id]
                                ? !!element.operations[stage.id].some(
                                    (elementOperation) => {
                                      if (!elementOperation.value) return false;

                                      return true;
                                    }
                                  )
                                : false;
                            }, false);
                        });
                      }, false);
                    }, false);

                return {
                  isChecked,
                  isSemiChecked,
                  isDisabled,
                };
              },
              building: () => {
                const currentBuilding = measuresList.find((building) => {
                  return building.id.toString() === record.id.toString();
                });

                const isDisabled =
                  !currentBuilding?.groups?.length ||
                  currentBuilding.groups.every((group) => {
                    return (
                      !group?.elements?.length ||
                      group.elements.every((element) => {
                        if (!element.operations?.[stage.id]) return false;

                        if (
                          element.operations[stage.id].every(
                            (elementOperation) => {
                              if (!elementOperation.value) return false;
                              if (!elementOperation.id) return false;

                              return true;
                            }
                          )
                        )
                          return true;

                        return false;
                      })
                    );
                  });

                const isChecked = measuresList.reduce(
                  (buildingResult, building) => {
                    if (!building.groups?.length) return buildingResult;

                    if (building.id.toString() !== record.id.toString())
                      return buildingResult;

                    if (
                      !building.groups.some((group) => !!group.elements?.length)
                    )
                      return buildingResult;

                    return building.groups.every((group) => {
                      if (!group.elements?.length) return true;

                      return !!group.elements.every((element) => {
                        return !!stagesList
                          .filter((stageItem) => stageItem.status === "active")
                          .reduce((result, stageItem) => {
                            if (result) return true;

                            if (stageItem.id.toString() !== stage.id.toString())
                              return result;

                            return element.operations?.[stage.id]
                              ? !!element.operations[stage.id].every(
                                  (elementOperation) => {
                                    if (!elementOperation.value) return false;

                                    return true;
                                  }
                                )
                              : false;
                          }, false);
                      });
                    }, false);
                  },
                  false
                );

                const isSemiChecked = isChecked
                  ? false
                  : measuresList.reduce((buildingResult, building) => {
                      if (!building.groups?.length) return buildingResult;

                      if (building.id.toString() !== record.id.toString())
                        return buildingResult;

                      if (
                        !building.groups.some(
                          (group) => !!group.elements?.length
                        )
                      )
                        return buildingResult;

                      return building.groups.some((group) => {
                        if (!group.elements?.length) return false;

                        return !!group.elements.some((element) => {
                          return !!stagesList
                            .filter(
                              (stageItem) => stageItem.status === "active"
                            )
                            .reduce((result, stageItem) => {
                              if (result) return true;

                              if (
                                stageItem.id.toString() !== stage.id.toString()
                              )
                                return result;

                              return element.operations?.[stage.id]
                                ? !!element.operations[stage.id].some(
                                    (elementOperation) => {
                                      if (!elementOperation.value) return false;

                                      return true;
                                    }
                                  )
                                : false;
                            }, false);
                        });
                      }, false);
                    }, false);

                return { isChecked, isSemiChecked, isDisabled };
              },
              default: () => {
                return {
                  isChecked: false,
                  isSemiChecked: false,
                  isDisabled: false,
                };
              },
            }[record.type || "default"]();

            return (
              <div>
                <Space size="small">
                  <Checkbox
                    onChange={(event) =>
                      handleChangeStageValue({
                        buildingId:
                          record.type === "building" ? record.id : undefined,
                        groupId:
                          record.type === "group" ? record.id : undefined,
                        elementId:
                          record.type === "element" ? record.id : undefined,
                        stageId: stage.id,
                        value: event.target.checked,
                      })
                    }
                    checked={isChecked}
                    indeterminate={isSemiChecked}
                    disabled={isInProcess || isDisabled}
                  />
                </Space>
              </div>
            );
          },
        });
      }

      if (stage?.is_operations_expanded) {
        const children: ColumnsType = [];

        if (stage.operations)
          stage.operations.forEach((operation) => {
            children.push({
              title: `${operation.name} (${operation.completion_percentage}%)`,
              key: `stage-operation-${operation.id}`,
              render: (text: any, untypedRecord: unknown, index) => {
                const record = untypedRecord as MeasureTableElementType;

                const { isChecked, isSemiChecked, isDisabled } = {
                  element: () => {
                    if (!record.operations)
                      return {
                        isChecked: false,
                        isSemiChecked: false,
                        isDisabled: false,
                      };

                    const isChecked = record.operations?.[stage.id]
                      ? !!record.operations[stage.id].find(
                          (recordOperation) => {
                            if (
                              recordOperation.stage_operation_id.toString() !==
                              operation.id.toString()
                            )
                              return false;

                            if (!recordOperation.value) return false;

                            return true;
                          }
                        )
                      : false;

                    const isDisabled = record.operations?.[stage.id]
                      ? !!record.operations[stage.id].find(
                          (recordOperation) => {
                            if (
                              recordOperation.stage_operation_id.toString() !==
                              operation.id.toString()
                            )
                              return false;

                            if (!recordOperation.value) return false;
                            if (!recordOperation.id) return false;

                            return true;
                          }
                        )
                      : false;

                    return {
                      isChecked,
                      isSemiChecked: false,
                      isDisabled,
                    };
                  },
                  group: () => {
                    const currentGroup = measuresList
                      .flatMap((building) => building.groups || [])
                      .find(
                        (group) => group.id.toString() === record.id.toString()
                      );

                    const isDisabled =
                      !currentGroup ||
                      !currentGroup?.elements?.length ||
                      currentGroup.elements.every((element) => {
                        if (!element.operations?.[stage.id]) return false;

                        if (
                          element.operations[stage.id].find(
                            (elementOperation) => {
                              if (
                                elementOperation.stage_operation_id.toString() !==
                                operation.id.toString()
                              )
                                return false;

                              if (!elementOperation.value) return false;
                              if (!elementOperation.id) return false;

                              return true;
                            }
                          )
                        )
                          return true;

                        return false;
                      });

                    const isChecked = measuresList.reduce(
                      (buildingResult, building) => {
                        if (!building.groups?.length) return buildingResult;

                        //  Поскольку проверяется только 1 group, если в результате был успех хоть раз, возвращаем true
                        if (buildingResult) return true;

                        return building.groups.reduce((groupResult, group) => {
                          if (group.id.toString() !== record.id.toString())
                            return groupResult;

                          if (!group.elements?.length) return groupResult;

                          //  Поскольку выше сравнение с record.id, проверен будет 1 group
                          return !!group.elements.every((element) => {
                            return !!stagesList
                              .filter(
                                (stageItem) => stageItem.status === "active"
                              )
                              .reduce((result, stageItem) => {
                                if (result) return true;

                                if (
                                  stageItem.id.toString() !==
                                  stage.id.toString()
                                )
                                  return result;

                                return element.operations?.[stage.id]
                                  ? !!element.operations[stage.id].find(
                                      (elementOperation) => {
                                        if (
                                          elementOperation.stage_operation_id.toString() !==
                                          operation.id.toString()
                                        )
                                          return false;

                                        if (!elementOperation.value)
                                          return false;

                                        return true;
                                      }
                                    )
                                  : false;
                              }, false);
                          });
                        }, false);
                      },
                      false
                    );

                    const isSemiChecked = isChecked
                      ? false
                      : measuresList.reduce((buildingResult, building) => {
                          if (!building.groups?.length) return buildingResult;

                          //  Поскольку проверяется только 1 group, если в результате был успех хоть раз, возвращаем true
                          if (buildingResult) return true;

                          //  Поскольку выше сравнение с record.id, проверен будет 1 group
                          return building.groups.reduce(
                            (groupResult, group) => {
                              if (group.id.toString() !== record.id.toString())
                                return groupResult;

                              if (!group.elements?.length) return groupResult;

                              return !!group.elements.some((element) => {
                                return !!stagesList
                                  .filter(
                                    (stageItem) => stageItem.status === "active"
                                  )
                                  .reduce((result, stageItem) => {
                                    if (result) return true;

                                    if (
                                      stageItem.id.toString() !==
                                      stage.id.toString()
                                    )
                                      return result;

                                    return element.operations?.[stage.id]
                                      ? !!element.operations[stage.id].find(
                                          (elementOperation) => {
                                            if (
                                              elementOperation.stage_operation_id.toString() !==
                                              operation.id.toString()
                                            )
                                              return false;

                                            if (!elementOperation.value)
                                              return false;

                                            return true;
                                          }
                                        )
                                      : false;
                                  }, false);
                              });
                            },
                            false
                          );
                        }, false);

                    return {
                      isChecked,
                      isSemiChecked,
                      isDisabled,
                    };
                  },
                  building: () => {
                    const currentBuilding = measuresList.find((building) => {
                      return building.id.toString() === record.id.toString();
                    });

                    const isDisabled =
                      !currentBuilding?.groups?.length ||
                      currentBuilding.groups.every((group) => {
                        return (
                          !group?.elements?.length ||
                          group.elements.every((element) => {
                            if (!element.operations?.[stage.id]) return false;

                            if (
                              element.operations[stage.id].find(
                                (elementOperation) => {
                                  if (
                                    elementOperation.stage_operation_id.toString() !==
                                    operation.id.toString()
                                  )
                                    return false;

                                  if (!elementOperation.value) return false;
                                  if (!elementOperation.id) return false;

                                  return true;
                                }
                              )
                            )
                              return true;

                            return false;
                          })
                        );
                      });

                    const isChecked = measuresList.reduce(
                      (buildingResult, building) => {
                        if (!building.groups?.length) return buildingResult;

                        if (building.id.toString() !== record.id.toString())
                          return buildingResult;

                        if (
                          !building.groups.some(
                            (group) => !!group.elements?.length
                          )
                        )
                          return buildingResult;

                        return building.groups.every((group) => {
                          if (!group.elements?.length) return true;

                          return !!group.elements.every((element) => {
                            return !!stagesList
                              .filter(
                                (stageItem) => stageItem.status === "active"
                              )
                              .reduce((result, stageItem) => {
                                if (result) return true;

                                if (
                                  stageItem.id.toString() !==
                                  stage.id.toString()
                                )
                                  return result;

                                return element.operations?.[stage.id]
                                  ? !!element.operations[stage.id].find(
                                      (elementOperation) => {
                                        if (
                                          elementOperation.stage_operation_id.toString() !==
                                          operation.id.toString()
                                        )
                                          return false;

                                        if (!elementOperation.value)
                                          return false;

                                        return true;
                                      }
                                    )
                                  : false;
                              }, false);
                          });
                        }, false);
                      },
                      false
                    );

                    const isSemiChecked = isChecked
                      ? false
                      : measuresList.reduce((buildingResult, building) => {
                          if (!building.groups?.length) return buildingResult;

                          if (building.id.toString() !== record.id.toString())
                            return buildingResult;

                          if (
                            !building.groups.some(
                              (group) => !!group.elements?.length
                            )
                          )
                            return buildingResult;

                          return building.groups.some((group) => {
                            if (!group.elements?.length) return false;

                            return !!group.elements.some((element) => {
                              return !!stagesList
                                .filter(
                                  (stageItem) => stageItem.status === "active"
                                )
                                .reduce((result, stageItem) => {
                                  if (result) return true;

                                  if (
                                    stageItem.id.toString() !==
                                    stage.id.toString()
                                  )
                                    return result;

                                  return element.operations?.[stage.id]
                                    ? !!element.operations[stage.id].find(
                                        (elementOperation) => {
                                          if (
                                            elementOperation.stage_operation_id.toString() !==
                                            operation.id.toString()
                                          )
                                            return false;

                                          if (!elementOperation.value)
                                            return false;

                                          return true;
                                        }
                                      )
                                    : false;
                                }, false);
                            });
                          }, false);
                        }, false);

                    return { isChecked, isSemiChecked, isDisabled };
                  },
                  default: () => {
                    return {
                      isChecked: false,
                      isSemiChecked: false,
                      isDisabled: false,
                    };
                  },
                }[record.type || "default"]();

                return (
                  <div>
                    <Space size="small">
                      <Checkbox
                        onChange={(event) =>
                          handleChangeStageValue({
                            buildingId:
                              record.type === "building"
                                ? record.id
                                : undefined,
                            groupId:
                              record.type === "group" ? record.id : undefined,
                            elementId:
                              record.type === "element" ? record.id : undefined,
                            operationId: operation.id,
                            stageId: stage.id,
                            value: event.target.checked,
                          })
                        }
                        checked={isChecked}
                        indeterminate={isSemiChecked}
                        disabled={isInProcess || isDisabled}
                      />
                    </Space>
                  </div>
                );
              },
            });
          });

        result.push({
          title: (
            <>
              <div className={styles.stageTitle}>
                {stage.short_name ? stage.short_name : stage.name}
              </div>
              <Button
                type="primary"
                size="small"
                className={styles.expandButton}
                onClick={() => handleExpandOperations(stage.id, false)}
                disabled={!list.isLoaded || !!isInProcess}
              >
                Свернуть
              </Button>
            </>
          ),
          key: `stage-${stage.id}`,
          onHeaderCell: () => ({
            style: { verticalAlign: "top", textAlign: "left" },
          }),
          children: children,
        });
      }
    });

    return result;
  }, [
    isInProcess,
    measuresList,
    stagesList,
    list.isLoaded,
    handleChangeChildrenVisibility,
    handleChangeStageValue,
    handleExpandOperations,
  ]);

  const tableData = useMemo(() => {
    const result: MeasureTableElementType[] = [];

    measuresList.forEach((measure) => {
      const measureData = { ...measure, groups: undefined };

      const buildingUnits: MeasureTableElementType["units"] = [];
      const groups: MeasureTableElementType[] = [];

      if (measure?.groups) {
        measure.groups.forEach((group) => {
          const groupData = { ...group };

          const groupUnits: MeasureTableElementType["units"] = [];
          const elements: MeasureTableElementType[] = [];

          if (group?.elements) {
            group.elements.forEach((element) => {
              const elementData = { ...element };

              if (measure.is_children_visible)
                if (group.is_children_visible)
                  elements.push({
                    ...elementData,
                    type: "element",
                    index: `element-${elementData.id}`,
                    is_children_visible: false,
                  });

              if (elementData.units?.length) {
                elementData.units.forEach((unit) => {
                  const groupUnitIndex = groupUnits.findIndex((groupUnit) => {
                    return groupUnit.id === unit.id;
                  });

                  if (groupUnitIndex === -1) {
                    groupUnits.push({ ...unit });
                  }

                  if (groupUnitIndex !== -1) {
                    const groupUnitValue = groupUnits[groupUnitIndex].value;
                    const unitValue = unit.value;

                    groupUnits[groupUnitIndex].value = (
                      (groupUnitValue ? parseFloat(groupUnitValue) : 0) +
                      (unitValue ? parseFloat(unitValue) : 0)
                    )
                      .toFixed(2)
                      .toString();
                  }
                });
              }
            });
          }

          if (measure.is_children_visible)
            groups.push({
              ...groupData,
              type: "group",
              index: `group-${groupData.id}`,
              units: groupUnits?.length ? [...groupUnits] : undefined,
            });

          if (groupUnits?.length) {
            groupUnits.forEach((groupUnit) => {
              const buildingUnitIndex = buildingUnits.findIndex(
                (buildingUnit) => {
                  return buildingUnit.id === groupUnit.id;
                }
              );

              if (buildingUnitIndex === -1) {
                buildingUnits.push({ ...groupUnit });
              }

              if (buildingUnitIndex !== -1) {
                const buildingUnitValue =
                  buildingUnits[buildingUnitIndex].value;
                const unitValue = groupUnit.value;

                buildingUnits[buildingUnitIndex].value = (
                  (buildingUnitValue ? parseFloat(buildingUnitValue) : 0) +
                  (unitValue ? parseFloat(unitValue) : 0)
                )
                  .toFixed(2)
                  .toString();
              }
            });
          }

          groups.push(...elements);
        });
      }

      result.push({
        ...measureData,
        type: "building",
        index: `building-${measureData.id}`,
        units: buildingUnits?.length ? [...buildingUnits] : undefined,
      });

      result.push(...groups);
    });

    return result;
  }, [measuresList]);

  return (
    <MobileInnerWrapper>
      <Form onFinish={handleFinish}>
        <div className={styles.titleBlock}>
          <Title level={3} className={styles.title}>
            Подтверждение объема
          </Title>
        </div>
        <Form.Item className={styles.select}>
          <Select
            value={
              !!(!!list.isLoaded && !!list.data?.length)
                ? selectedObjectIndex
                : "Загрузка..."
            }
            onChange={handleChangeObjectIndex}
            disabled={!list.isLoaded || !!isInProcess}
          >
            {!!(!!list.isLoaded && !!list.data?.length) &&
              list.data.map((item, index) => (
                <Option key={index} value={index}>
                  {item.name}
                </Option>
              ))}
          </Select>
        </Form.Item>
        <Form.Item>
          <Table
            dataSource={tableData}
            columns={columns}
            rowKey="id"
            pagination={false}
            loading={!list.isLoaded}
            styles={styles}
          />
        </Form.Item>
        <Form.Item>
          <Button
            type="primary"
            size="large"
            className={styles.button}
            disabled={!list.isLoaded}
            htmlType="submit"
            loading={isInProcess}
          >
            Сохранить
          </Button>
        </Form.Item>
        {!!isError && (
          <Form.Item>
            <Text type="danger">Не удалось сохранить объект</Text>
          </Form.Item>
        )}
      </Form>
    </MobileInnerWrapper>
  );
};

export default Page;
